import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { TrendingUp, TrendingDown, DollarSign, Users, MapPin, ShoppingCart, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
const formatCompact = (amount) => {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
  return amount.toString();
};

const PIE_COLORS = ['#059669', '#d97706', '#7c3aed', '#2563eb', '#64748b', '#0891b2', '#f59e0b', '#0d9488', '#e11d48', '#6b7280'];

const BOFinancialDashboard = () => {
  const { user, role } = useBOAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await boAPI.getFinancialDashboard(); setData(res.data); }
      catch (e) { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;
  if (!data) return <BOLayout><div className="text-center py-20 text-neutral-500">Unable to load dashboard data</div></BOLayout>;

  const categoryData = Object.entries(data.category_breakdown || {}).map(([key, value], i) => ({
    name: (data.category_labels || {})[key] || key,
    value,
    color: PIE_COLORS[i % PIE_COLORS.length],
  })).sort((a, b) => b.value - a.value);

  const profitPositive = data.total_profit >= 0;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-financial-dashboard">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Financial Dashboard</h1>
          <p className="text-neutral-500 mt-1">Business performance overview</p>
        </div>

        {/* Top-Level Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500">Total Revenue</p>
                  <p className="text-xl lg:text-2xl font-bold text-green-700" data-testid="metric-revenue">{formatNaira(data.total_revenue)}</p>
                  <p className="text-xs text-neutral-400 mt-1">This month: {formatNaira(data.month_revenue)}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500">Total Expenses</p>
                  <p className="text-xl lg:text-2xl font-bold text-red-600" data-testid="metric-expenses">{formatNaira(data.total_expenses)}</p>
                  <p className="text-xs text-neutral-400 mt-1">This month: {formatNaira(data.month_expenses)}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-neutral-border shadow-card ${profitPositive ? '' : 'border-red-200'}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500">Net Profit</p>
                  <p className={`text-xl lg:text-2xl font-bold ${profitPositive ? 'text-primary-900' : 'text-red-600'}`} data-testid="metric-profit">{formatNaira(data.total_profit)}</p>
                  <p className="text-xs text-neutral-400 mt-1">This month: {formatNaira(data.month_profit)}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl ${profitPositive ? 'bg-primary-100' : 'bg-red-100'} flex items-center justify-center`}>
                  <DollarSign className={`h-5 w-5 ${profitPositive ? 'text-primary-700' : 'text-red-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500">Outstanding</p>
                  <p className="text-xl lg:text-2xl font-bold text-amber-600" data-testid="metric-outstanding">{formatNaira(data.outstanding)}</p>
                  <p className="text-xs text-neutral-400 mt-1">From {formatNaira(data.total_sales_value)} in sales</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="h-5 w-5 text-primary-600" />
              <div><p className="text-lg font-bold">{data.total_clients}</p><p className="text-xs text-neutral-500">Active Clients</p></div>
            </CardContent>
          </Card>
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="h-5 w-5 text-primary-600" />
              <div><p className="text-lg font-bold">{data.sold_plots}/{data.total_plots}</p><p className="text-xs text-neutral-500">Plots Sold</p></div>
            </CardContent>
          </Card>
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <ShoppingCart className="h-5 w-5 text-primary-600" />
              <div><p className="text-lg font-bold">{data.active_sales}</p><p className="text-xs text-neutral-500">Active Sales</p></div>
            </CardContent>
          </Card>
          <Card className="border-neutral-border shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div><p className="text-lg font-bold">{data.total_revenue > 0 ? Math.round((data.total_revenue / data.total_sales_value) * 100) : 0}%</p><p className="text-xs text-neutral-500">Collection Rate</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cash Flow Chart */}
          <Card className="border-neutral-border shadow-card lg:col-span-2">
            <CardHeader><CardTitle className="text-lg">Monthly Cash Flow (Last 12 Months)</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[320px]" data-testid="cashflow-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly_data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => `${formatCompact(v)}`} tickLine={false} axisLine={false} />
                    <Tooltip
                      formatter={(value) => formatNaira(value)}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                    />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#059669" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#dc2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Expense Breakdown */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Expense Breakdown</CardTitle></CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <>
                  <div className="h-[200px]" data-testid="expense-pie-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                          {categoryData.map((entry) => <Cell key={`cell-${entry.name}`} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(value) => formatNaira(value)} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 mt-3">
                    {categoryData.map((cat) => (
                      <div key={`legend-${cat.name}`} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span className="text-neutral-600 truncate max-w-[120px]">{cat.name}</span>
                        </div>
                        <span className="font-medium">{formatNaira(cat.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-neutral-500 text-sm text-center py-8">No expenses recorded yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Profit Trend */}
        <Card className="border-neutral-border shadow-card">
          <CardHeader><CardTitle className="text-lg">Monthly Profit Trend</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[250px]" data-testid="profit-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly_data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => `${formatCompact(v)}`} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(value) => formatNaira(value)} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
                  <Bar dataKey="profit" name="Profit" fill="#1e3a5f" radius={[4, 4, 0, 0]}>
                    {(data.monthly_data || []).map((entry) => (
                      <Cell key={`profit-cell-${entry.month}`} fill={entry.profit >= 0 ? '#059669' : '#dc2626'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </BOLayout>
  );
};

export default BOFinancialDashboard;
